import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import {
	isLocation,
	isInboundLocation,
	updateLocationBays,
	getScannedItemByProductId,
	validateScanAllowed,
	receiveStatus,
	createReceiveLineItem,
	saveEventHistoryToList,
	modals,
	createReceive,
	loadingDialogClosed,
	addOrUpdateReceiveToReceivesList,
} from "../utils/receiveUtilExports";
import { v4 as uuidv4 } from "uuid";
import { AssignLocationProps, LoadingDialogState, OnReceivedQuantityChangeProps, ScanEvent } from "../types";
import { CurrentScannedProduct, emptyCurrentScannedProduct } from "../../../util/emptyObjectsUtil";
import PageContext from "../../../components/Common/PageContext";
import { ReceiveType } from "../../../util/receiveTypeMapping";
import { PurchaseOrder, PurchaseOrderLineItem } from "../../../common/PurchaseOrder";
import { UserContext } from "../../../providers/user";
import { Receive, ReceiveLineItem } from "../../../common/Receive";
import { usePostLineItemReceived } from "../../../common/api/usePostLineItemReceived";
import { ManualInvoiceEditableFields } from "../../../components/Invoice/types";
import { usePostReceive } from "../../../common/api/usePostReceive";
import { AdditionalProductDetails } from "../../../util/ProductUtilities";
import { containsLockedReceiveForStatus } from "../../../util/receiveUtil";
import { receiveType as receiveTypes } from "../../../util/receiveTypeMapping";
import { appInsights } from "../../../services";
import {EdiInvoiceLineItem} from "../../../common/EdiInvoice";

export interface UseScanParams {
	// Required parameters
	purchaseOrder: PurchaseOrder | undefined;
	receiveType: ReceiveType;
	receive: Receive | undefined;
	productDetails: AdditionalProductDetails[];
	invoiceFields: ManualInvoiceEditableFields;
	receives: Receive[];
	setReceives: Dispatch<SetStateAction<Receive[]>>;
	setReceive: (receive: Receive) => void;
	onReceivedQuantityChange: (params: OnReceivedQuantityChangeProps) => Receive;
	setCurrentScannedProduct: (state: CurrentScannedProduct) => void;
	// Optional parameters
	invoiceLineItems?: EdiInvoiceLineItem[]; // The invoice line items
	inboundLocation?: string;
	locationBays?: string[];
	activeTab?: number;
	assignLocation?: AssignLocationProps;
	setModal?: (modal: any) => void;
	setIsExpanded?: (expanded: any) => void;
	setInboundLocation?: (location: string) => void;
	setAssignLocation?: (location: AssignLocationProps) => void;
	setActiveTab?: (tab: number) => void;
	onCloseModal?: () => void;
	setLoadingDialog?: (state: LoadingDialogState) => void;
	enableAssignIbLocation?: boolean;
	selectedCommercialAccount?: string;
	initialReceiveStatus?: string; // The initial status for a new receive (defaults to inProgress)
	isOutstandingEdiInvoiceReceive?: boolean;
}

export const useScan = ({
	purchaseOrder,
	receiveType,
	receive,
	productDetails,
	invoiceFields,
	receives,
	setReceives,
	setReceive,
	onReceivedQuantityChange,
	setCurrentScannedProduct,
	// Optional with defaults
	invoiceLineItems = [],
	inboundLocation = '',
	locationBays = [],
	activeTab = 1,
	assignLocation = { isModalOpen: false, sku: '', currentLocation: '', scannedLocation: '', context: 'manual' as const },
	setModal = () => {},
	setIsExpanded = () => {},
	setInboundLocation = () => {},
	setAssignLocation = () => {},
	setActiveTab = () => {},
	onCloseModal = () => {},
	setLoadingDialog = () => {},
	enableAssignIbLocation = false,
	selectedCommercialAccount = '',
	initialReceiveStatus = receiveStatus.inProgress,
	isOutstandingEdiInvoiceReceive = false,
}: UseScanParams) => {
	const [pageContext] = useContext(PageContext);
	const { graphProfile } = useContext(UserContext);
	const [, postLineItemReceived] = usePostLineItemReceived();
	const [, postReceiveAsync] = usePostReceive(true);
	const newReceiveId = useMemo(() => uuidv4(), []);

	// Keep track of the latest receive state with a ref to avoid stale closures
	const latestReceiveRef = useRef<Receive | undefined>(receive);

	// Update ref when receive changes
	useEffect(() => {
		latestReceiveRef.current = receive;
	}, [receive]);

	// Queue to manage sequential processing of scans
	const pendingScanQueue = useRef<{ inputValue: string; qty?: number }[]>([]);
	const isProcessingScan = useRef(false);

	// Core save logic - updates state and fires API call
	const saveLineItemUpdate = useCallback(
		async (quantity: number, purchaseOrderLineItem: PurchaseOrderLineItem) => {
			if (!purchaseOrder) return;

			// Always use the latest receive state from the ref
			let receiveToUpdate: Receive;
			const isNewReceive = !latestReceiveRef.current;
			if (!latestReceiveRef.current) {
				receiveToUpdate = {
					...createReceive(purchaseOrder, graphProfile, receiveType, newReceiveId),
					status: initialReceiveStatus,
					invoiceNumber: invoiceFields.invoiceNumber!,
					invoiceDate: invoiceFields.invoiceDate!,
					receivedDate: invoiceFields.receivedDate!,
					inventoryStatus: selectedCommercialAccount,
					notes: invoiceFields.notes!,
					totalInvoicedFreightEx: invoiceFields.totalInvoicedFreightEx ?? 0,
					totalInvoicedFreightInc: invoiceFields.totalInvoicedFreightInc ?? 0,
					totalInvoicedFreightTax: invoiceFields.totalInvoicedFreightTax ?? 0,
				};

				// For EDI receives, pre-populate all line items from the invoice
				if (receiveType === receiveTypes.edi && invoiceLineItems.length > 0) {
					receiveToUpdate.receiveLineItems = invoiceLineItems.map(invoiceLineItem => {
						const poLineItem = purchaseOrder.lineItems.find(poLineItem => poLineItem.sku === invoiceLineItem.sku);
						return {
							...createReceiveLineItem(poLineItem, 0, inboundLocation),
							invoicedQuantity: invoiceLineItem.quantity,
						}
					});
				}
			} else {
				receiveToUpdate = {
					...latestReceiveRef.current,
					invoiceNumber: invoiceFields.invoiceNumber!,
					invoiceDate: invoiceFields.invoiceDate!,
					receivedDate: invoiceFields.receivedDate!,
					inventoryStatus: selectedCommercialAccount,
					notes: invoiceFields.notes!,
				};
			}

			let receiveLineItem = receiveToUpdate.receiveLineItems.find(
				(rli: ReceiveLineItem) => rli.sku === purchaseOrderLineItem.sku
			);

			if (!receiveLineItem) {
				receiveLineItem = createReceiveLineItem(purchaseOrderLineItem, 0, inboundLocation);
				receiveToUpdate.receiveLineItems.push(receiveLineItem);
			}

			receiveToUpdate = onReceivedQuantityChange({
				receive: receiveToUpdate,
				sku: receiveLineItem.sku,
				newQuantity: quantity,
			});

			// Update the ref immediately
			latestReceiveRef.current = receiveToUpdate;

			// Update React state
			setReceive(receiveToUpdate);
			setReceives((prevReceives) => addOrUpdateReceiveToReceivesList(prevReceives, receiveToUpdate));

			try {
				if (isNewReceive) {
					// Auto-create the Receive on the first scan
					setLoadingDialog({ isOpen: true, message: "Creating Receive..." });
					await postReceiveAsync(receiveToUpdate);
				} else {
					void postLineItemReceived({
						...receiveToUpdate.receiveLineItems.find((rli) => rli.sku === receiveLineItem.sku)!,
						receiveId: receiveToUpdate.id,
						modifiedBy: graphProfile.displayName,
						modifiedById: graphProfile.staffId,
						modifiedDate: new Date().toISOString(),
					});
				}
			} finally {
				setLoadingDialog(loadingDialogClosed);
			}

			if (
				enableAssignIbLocation &&
				receiveLineItem.inboundLocation &&
				receiveLineItem.inboundLocation !== inboundLocation
			) {
				setAssignLocation({
					...assignLocation,
					sku: receiveLineItem.sku,
					currentLocation: receiveLineItem.inboundLocation,
					context: "scanned",
					scannedLocation: "",
					isModalOpen: true,
				});
			}
		},
		[
			purchaseOrder,
			graphProfile,
			receiveType,
			newReceiveId,
			invoiceFields,
			selectedCommercialAccount,
			invoiceLineItems,
			inboundLocation,
			onReceivedQuantityChange,
			setReceive,
			setReceives,
			postReceiveAsync,
			postLineItemReceived,
			setLoadingDialog,
			enableAssignIbLocation,
			assignLocation,
			setAssignLocation,
		initialReceiveStatus,
		]
	);

	const processSingleScan = useCallback(
		async (inputValue: string, qty?: number) => {
			const currentProduct = { productId: inputValue, qty };
			if (!purchaseOrder) {
				return;
			}

			const putAwayLocations = pageContext.putAwayLocations || new Set<string>();
			if (enableAssignIbLocation && (isLocation(inputValue) || putAwayLocations.has(inputValue))) {
				if (
					isInboundLocation(inputValue, purchaseOrder.shipToLocation.locationId) ||
					putAwayLocations.has(inputValue)
				) {
					updateLocationBays(inputValue, locationBays);
					setInboundLocation(inputValue);
					setCurrentScannedProduct(emptyCurrentScannedProduct);
				} else {
					setModal({
						...modals.issueValidatingLocationBay,
						onPrimaryButtonClick: onCloseModal,
					});
				}
				return;
			}

			const purchaseOrderLineItem: PurchaseOrderLineItem | null = getScannedItemByProductId(
				purchaseOrder.lineItems,
				currentProduct.productId,
				productDetails
			);

			try {
				setLoadingDialog({ isOpen: true, message: "Validating scan..." });

				const isPurchaseOrderLocked = containsLockedReceiveForStatus(
					receives,
					graphProfile.staffId,
					receiveStatus.inProgress
				);
				const validationResults = await validateScanAllowed(
					latestReceiveRef.current?.id,
					receiveType,
					purchaseOrder,
					invoiceFields,
					receiveType === receiveTypes.manual && isPurchaseOrderLocked,
					purchaseOrderLineItem,
					receives,
					enableAssignIbLocation,
					inboundLocation,
					latestReceiveRef.current?.status,
					isOutstandingEdiInvoiceReceive
				);

				if (!validationResults.isValid) {
					setModal({
						isOpen: true,
						title: "Issue scanning",
						paragraphs: [validationResults.reason],
						primaryButtonName: "Return to scan",
						onPrimaryButtonClick: onCloseModal,
					});
					return;
				}
			} catch (error) {
				appInsights.trackException({
					exception: error as Error,
					properties: {
						productId: currentProduct.productId,
						receiveType: receiveType,
						purchaseOrderId: purchaseOrder.id,
						inboundLocation: inboundLocation,
						userId: graphProfile.staffId,
						userName: graphProfile.displayName,
						context: "ScanValidation",
					},
				});

				setModal({
					isOpen: true,
					title: "Validation Error",
					paragraphs: ["An error occurred while validating the scan. Please try again."],
					primaryButtonName: "Return to scan",
					onPrimaryButtonClick: onCloseModal,
				});
				return;
			} finally {
				setLoadingDialog({ isOpen: false, message: "" });
			}

			setCurrentScannedProduct(emptyCurrentScannedProduct);
			saveEventHistoryToList({
				date: new Date(),
				staffId: graphProfile.staffId,
				staffName: graphProfile.displayName,
				purchaseOrderNumber: purchaseOrder?.sourceId,
				model: currentProduct.productId,
				qty: !currentProduct.qty ? 1 : currentProduct.qty,
				description: purchaseOrderLineItem!.description,
				action: "scan",
				invoiceNumber: invoiceFields.invoiceNumber,
				sku: purchaseOrderLineItem!.sku,
				inboundLocation: inboundLocation,
			});

			setIsExpanded({
				header: false,
				invoiceDetails: false,
			});

			// Use the extracted save logic
			const currentReceiveLineItem = latestReceiveRef.current?.receiveLineItems.find(
				(rli: ReceiveLineItem) => rli.sku === purchaseOrderLineItem!.sku
			);
			const newQuantity = !currentProduct.qty ? (currentReceiveLineItem?.receivedQuantity || 0) + 1 : currentProduct.qty;

			await saveLineItemUpdate(newQuantity, purchaseOrderLineItem!);
		},
		[
			purchaseOrder,
			productDetails,
			receives,
			inboundLocation,
			locationBays,
			pageContext.putAwayLocations,
			graphProfile,
			assignLocation,
			enableAssignIbLocation,
			invoiceFields,
			onCloseModal,
			receiveType,
			setAssignLocation,
		initialReceiveStatus,
			setInboundLocation,
			setLoadingDialog,
			setModal,
			setIsExpanded,
			setCurrentScannedProduct,
			saveLineItemUpdate,
		]
	);

	// Process scans one at a time
	const processNextScan = useCallback(async () => {
		if (pendingScanQueue.current.length === 0 || isProcessingScan.current) {
			return;
		}

		isProcessingScan.current = true;
		const nextScan = pendingScanQueue.current.shift();

		try {
			await processSingleScan(nextScan!.inputValue, nextScan!.qty);
		} finally {
			isProcessingScan.current = false;
			// Process next item in queue if any
			if (pendingScanQueue.current.length > 0) {
				void processNextScan();
			}
		}
	}, [processSingleScan]);

	const onSaveItemClick = useCallback(
		async (inputValue: string, qty?: number) => {
			// Add scan to queue and process
			pendingScanQueue.current.push({ inputValue, qty });
			if (!isProcessingScan.current) {
				void processNextScan();
			}
		},
		[processNextScan]
	);

	const onScan = useCallback(
		async (e: ScanEvent) => {
			if (assignLocation.isModalOpen) {
				setAssignLocation({ ...assignLocation, scannedLocation: e.data.BarcodeValue });
				return;
			}

			if (activeTab !== 1) {
				setActiveTab(1);
			}
			window.scrollTo(0, 0);

			await onSaveItemClick(e.data.BarcodeValue);
		},
		[assignLocation, activeTab, setActiveTab, onSaveItemClick, setAssignLocation]
	);

	const onDataChange = useCallback(
		async (data: any) => {
			setCurrentScannedProduct({
				qty: data.qty,
				productId: data.productId,
			});
		},
		[setCurrentScannedProduct]
	);

	useEffect(() => {
		const handleScanEvent = (event: Event) => {
			const scanEvent = event as unknown as ScanEvent;
			onScan(scanEvent);
		};

		document.addEventListener("onScan", handleScanEvent);

		return () => {
			document.removeEventListener("onScan", handleScanEvent);
		};
	}, [onScan]);

	// Direct save function for manual quantity updates (bypasses validation)
	const saveQuantity = useCallback(
		async (sku: string, quantity: number) => {
			if (!purchaseOrder) return;

			const purchaseOrderLineItem = purchaseOrder.lineItems.find(item => item.sku === sku);
			if (!purchaseOrderLineItem) return;

			await saveLineItemUpdate(quantity, purchaseOrderLineItem);
		},
		[purchaseOrder, saveLineItemUpdate]
	);

	return {
		onScan,
		onSaveItemClick,
		onDataChange,
		saveQuantity,
	};
};
